import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ById } from 'common/types';
import { Box } from '@mui/material';

import { useTranslation } from 'services/localization/useTranslation';
import { Routes } from 'routing';

import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';

import CategoriesTable from 'components/CategoriesTable';
import SearchBar from 'components/SearchBar';
import { getTranslation } from 'common/modules/translations';
import LinkButton from 'common/components/LinkButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import EmptyState from 'common/components/states/EmptyState';

const ProductCategories = () => {
	const { t } = useTranslation();

	const categories = useSelector(StockSelectors.categoriesData);
	const loading = useSelector(StockSelectors.categoriesLoading);
	const lang = useSelector(LanguageSelectors.language);

	const [searchValue, setSearchValue] = useState<string>('');

	const categorySearchValuesById: ById<string> = useMemo(() => {
		return categories.reduce((result, category) => {
			result[category.id] = [
				getTranslation(category.name, lang)?.toLowerCase().trim() ?? '',
				getTranslation(category.description, lang)?.toLowerCase().trim() ?? '',
			].join('|');
			return result;
		}, {} as ById<string>);
	}, [categories, lang]);

	const filteredCategories =
		categories?.filter((c) => {
			const categorySearchValue = categorySearchValuesById[c.id] ?? '';
			const trimmedSearchValue = searchValue.toLowerCase().trim();
			return categorySearchValue.indexOf(trimmedSearchValue) !== -1;
		}) ?? [];

	const hasCategories = categories.length > 0;

	const newCategoryButton = (
		<LinkButton to={Routes.CATALOG_CREATE_CATEGORY} variant="contained" color="primary">
			{t('products.newCategory', 'New category')}
		</LinkButton>
	);

	return (
		<>
			<SetPageTitle
				title={t('products.catalog', 'Catalog')}
				priority={2}
				styling={{ fluid: true }}
				rightContent={hasCategories ? newCategoryButton : undefined}
			/>
			{hasCategories ? (
				<Box>
					<Box flex={1}>
						<SearchBar
							onChange={setSearchValue}
							placeholder={t('rentals.search.searchForCategory', 'Search for a category')}
						/>
					</Box>

					<CategoriesTable
						categories={filteredCategories}
						searchValue={searchValue}
						loading={loading}
					/>
				</Box>
			) : (
				<EmptyState
					cta={newCategoryButton}
					title={t('CategoriesTable.noCategories', 'You have no categories')}
					description={t(
						'ProductCategories.emptyDescription',
						'Create categories to group together products in your shop and make it easier for your customers to find what they are looking for',
					)}
				/>
			)}
		</>
	);
};

export default ProductCategories;
